import React, { useEffect, useMemo, useRef, useState } from "react";
import { authAxios, API } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import MediaUploadInput from "@/components/MediaUploadInput";
import {
  Send, Users, MessageSquare, FileSpreadsheet, Sparkles, Check, X,
  Loader2, ChevronRight, ChevronLeft, RefreshCw, Trash2, Eye, Calendar, Clock,
  Image as ImageIcon, Film, Music, FileText, Ban,
} from "lucide-react";

const MEDIA_TYPES = [
  { id: "text", label: "Solo texto", icon: MessageSquare },
  { id: "image", label: "Imagen", icon: ImageIcon },
  { id: "video", label: "Video", icon: Film },
  { id: "audio", label: "Audio", icon: Music },
  { id: "document", label: "Documento", icon: FileText },
];

export default function BroadcastPage() {
  const [tab, setTab] = useState("new");
  const [campaigns, setCampaigns] = useState([]);
  const [refreshTick, setRefreshTick] = useState(0);
  const [detailOpen, setDetailOpen] = useState(null);

  // Wizard state
  const [step, setStep] = useState(1);
  const [campaignName, setCampaignName] = useState("");
  const [targetSource, setTargetSource] = useState("groups"); // groups|members|chats|upload
  const [selectedTargets, setSelectedTargets] = useState([]); // [{jid, phone, label}]
  const [groups, setGroups] = useState([]);
  const [groupSel, setGroupSel] = useState([]);
  const [chats, setChats] = useState([]);
  const [chatSel, setChatSel] = useState([]);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [message, setMessage] = useState("");
  const [mediaType, setMediaType] = useState("text");
  const [mediaUrl, setMediaUrl] = useState("");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Scheduling
  const [sendMode, setSendMode] = useState("now"); // "now" | "schedule"
  const [scheduleDate, setScheduleDate] = useState(""); // YYYY-MM-DD
  const [scheduleTime, setScheduleTime] = useState(""); // HH:mm
  const browserTz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC", []);
  const [scheduleTz, setScheduleTz] = useState(browserTz);
  const tzOptions = useMemo(() => Array.from(new Set([
    browserTz,
    "America/Argentina/Buenos_Aires",
    "America/Mexico_City",
    "America/Bogota",
    "America/Lima",
    "America/Santiago",
    "America/Sao_Paulo",
    "UTC",
  ])), [browserTz]);

  // Drag & drop for Excel/CSV
  const uploadRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFilePicked = async (file) => {
    if (!file) return;
    setUploading(true); setError("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await authAxios.post(`${API}/broadcast/sources/upload-list`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadResult(data);
      setSelectedTargets(data.items || []);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setUploading(false); }
  };

  // Initial loads
  useEffect(() => {
    Promise.all([
      authAxios.get(`${API}/groups`).catch(() => ({ data: [] })),
      authAxios.get(`${API}/broadcast/sources/chats`).catch(() => ({ data: [] })),
    ]).then(([g, c]) => { setGroups(g.data || []); setChats(c.data || []); });
  }, []);

  // History tab refresh
  useEffect(() => {
    if (tab !== "history") return;
    let live = true;
    const fetchAll = () => authAxios.get(`${API}/broadcast/campaigns`).then(({ data }) => live && setCampaigns(data || []));
    fetchAll();
    const t = setInterval(fetchAll, 5000);
    return () => { live = false; clearInterval(t); };
  }, [tab, refreshTick]);

  const expandGroupMembers = async () => {
    if (!groupSel.length) return;
    setLoadingMembers(true);
    try {
      const chatIds = groups.filter((g) => groupSel.includes(g.group_chat_id || g.id)).map((g) => g.group_chat_id || g.id);
      const { data } = await authAxios.post(`${API}/broadcast/sources/group-members`, { group_chat_ids: chatIds });
      setSelectedTargets(data || []);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setLoadingMembers(false); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    await handleFilePicked(file);
    e.target.value = "";
  };

  const onPickGroupsAsTargets = (ids) => {
    setGroupSel(ids);
    if (targetSource === "groups") {
      setSelectedTargets(ids.map((chatId) => {
        const g = groups.find((x) => (x.group_chat_id || x.id) === chatId);
        return {
          jid: chatId,
          phone: "",
          label: g?.group_name || g?.name || chatId,
        };
      }));
    }
  };

  const generateWithAI = async () => {
    if (!aiPrompt.trim()) return;
    setAiBusy(true); setError("");
    try {
      const { data } = await authAxios.post(`${API}/broadcast/ai-draft`, { prompt: aiPrompt });
      setMessage(data.text || "");
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setAiBusy(false); }
  };

  const submitCampaign = async () => {
    setSubmitting(true); setError("");
    try {
      let scheduled_at = null;
      if (sendMode === "schedule") {
        if (!scheduleDate || !scheduleTime) { throw new Error("Elegí fecha y hora para programar"); }
        // Interpret local datetime in the selected tz and convert to UTC ISO.
        // Trick: create a string like "2026-03-10 15:30:00" and use Intl to get the UTC instant.
        const localStr = `${scheduleDate}T${scheduleTime}:00`;
        const d = new Date(localStr);
        if (scheduleTz === browserTz) {
          scheduled_at = d.toISOString();
        } else {
          // Compute offset of selected tz at that moment
          const fmt = new Intl.DateTimeFormat("en-US", {
            timeZone: scheduleTz, year: "numeric", month: "2-digit", day: "2-digit",
            hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
          });
          const parts = Object.fromEntries(fmt.formatToParts(d).filter(p => p.type !== "literal").map(p => [p.type, p.value]));
          const tzAsUtc = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
          const offset = tzAsUtc - d.getTime();
          scheduled_at = new Date(d.getTime() - offset).toISOString();
        }
        const now = new Date();
        if (new Date(scheduled_at) < now) throw new Error("La fecha programada debe ser futura");
      }
      const payload = {
        name: campaignName, message, media_url: mediaUrl, media_type: mediaType,
        targets: selectedTargets, scheduled_at, timezone: scheduleTz,
      };
      await authAxios.post(`${API}/broadcast/campaigns`, payload);
      setStep(1); setCampaignName(""); setSelectedTargets([]); setGroupSel([]);
      setChatSel([]); setUploadResult(null); setMessage(""); setMediaUrl(""); setMediaType("text");
      setSendMode("now"); setScheduleDate(""); setScheduleTime("");
      setTab("history"); setRefreshTick((x) => x + 1);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setSubmitting(false); }
  };

  const targetsCount = selectedTargets.length;

  return (
    <div className="p-8 max-w-6xl" data-testid="broadcast-page">
      <div className="mb-6 flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-pink-500 flex items-center justify-center">
          <Send className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold">Difusión masiva</h1>
          <p className="text-sm text-slate-500">Envía un mensaje a varios grupos o contactos con delay humanizado (3-10s).</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="new" data-testid="tab-new">Nueva campaña</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Historial</TabsTrigger>
        </TabsList>

        {/* ==================== NEW CAMPAIGN ==================== */}
        <TabsContent value="new" className="mt-6">
          <Card>
            <CardContent className="p-6 space-y-6">
              {/* Stepper */}
              <div className="flex items-center gap-2 text-xs">
                {[1, 2, 3].map((n) => (
                  <React.Fragment key={n}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${step >= n ? "bg-amber-500 text-white" : "bg-slate-200 text-slate-500"}`} data-testid={`step-indicator-${n}`}>{n}</div>
                    {n < 3 && <div className={`flex-1 h-0.5 ${step > n ? "bg-amber-500" : "bg-slate-200"}`} />}
                  </React.Fragment>
                ))}
              </div>

              {/* Step 1 — Targets */}
              {step === 1 && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold">Destinatarios</h3>
                    <p className="text-sm text-slate-500">Elegí cómo querés armar la lista.</p>
                  </div>
                  <Tabs value={targetSource} onValueChange={(v) => { setTargetSource(v); setSelectedTargets([]); }}>
                    <TabsList>
                      <TabsTrigger value="groups" data-testid="src-groups"><Users className="w-4 h-4 mr-1.5" /> Grupos</TabsTrigger>
                      <TabsTrigger value="members" data-testid="src-members"><Users className="w-4 h-4 mr-1.5" /> Miembros de grupos</TabsTrigger>
                      <TabsTrigger value="chats" data-testid="src-chats"><MessageSquare className="w-4 h-4 mr-1.5" /> Chats del bot</TabsTrigger>
                      <TabsTrigger value="upload" data-testid="src-upload"><FileSpreadsheet className="w-4 h-4 mr-1.5" /> Subir Excel/CSV</TabsTrigger>
                    </TabsList>

                    <TabsContent value="groups" className="mt-4">
                      <p className="text-xs text-slate-500 mb-2">El mensaje se envía al chat de cada grupo seleccionado.</p>
                      <SelectableList
                        items={groups.map((g) => ({
                          id: g.group_chat_id || g.id,
                          label: g.group_name || g.name || g.group_chat_id,
                          sub: `${g.participant_count || 0} miembros${g.bot_is_admin ? " · admin" : ""}`,
                        }))}
                        selected={groupSel}
                        onToggle={(id) => onPickGroupsAsTargets(groupSel.includes(id) ? groupSel.filter((x) => x !== id) : [...groupSel, id])}
                        onAll={() => onPickGroupsAsTargets(groups.map((g) => g.group_chat_id || g.id))}
                        onClear={() => onPickGroupsAsTargets([])}
                        testid="groups-list"
                      />
                    </TabsContent>

                    <TabsContent value="members" className="mt-4 space-y-3">
                      <p className="text-xs text-slate-500">Elegí los grupos y exportamos los miembros como contactos individuales (1-a-1 DM).</p>
                      <SelectableList
                        items={groups.map((g) => ({
                          id: g.group_chat_id || g.id,
                          label: g.group_name || g.name || g.group_chat_id,
                          sub: `${g.participant_count || 0} miembros`,
                        }))}
                        selected={groupSel}
                        onToggle={(id) => setGroupSel(groupSel.includes(id) ? groupSel.filter((x) => x !== id) : [...groupSel, id])}
                        onAll={() => setGroupSel(groups.map((g) => g.group_chat_id || g.id))}
                        onClear={() => setGroupSel([])}
                        testid="member-groups-list"
                      />
                      <Button onClick={expandGroupMembers} disabled={!groupSel.length || loadingMembers} className="bg-slate-900" data-testid="expand-members-btn">
                        {loadingMembers ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
                        Expandir miembros ({groupSel.length} grupo{groupSel.length !== 1 ? "s" : ""})
                      </Button>
                    </TabsContent>

                    <TabsContent value="chats" className="mt-4">
                      <p className="text-xs text-slate-500 mb-2">Personas que ya chatearon con tu bot ({chats.length}).</p>
                      <SelectableList
                        items={chats.map((c) => ({ id: c.jid, label: c.label, sub: c.phone }))}
                        selected={chatSel}
                        onToggle={(id) => {
                          const next = chatSel.includes(id) ? chatSel.filter((x) => x !== id) : [...chatSel, id];
                          setChatSel(next);
                          setSelectedTargets(next.map((j) => {
                            const c = chats.find((x) => x.jid === j);
                            return { jid: j, phone: c?.phone || "", label: c?.label || j };
                          }));
                        }}
                        onAll={() => { const all = chats.map((c) => c.jid); setChatSel(all); setSelectedTargets(chats.map((c) => ({ jid: c.jid, phone: c.phone, label: c.label }))); }}
                        onClear={() => { setChatSel([]); setSelectedTargets([]); }}
                        testid="chats-list"
                      />
                    </TabsContent>

                    <TabsContent value="upload" className="mt-4 space-y-3">
                      <div
                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={async (e) => {
                          e.preventDefault();
                          setDragOver(false);
                          const file = e.dataTransfer.files?.[0];
                          await handleFilePicked(file);
                        }}
                        onClick={() => !uploading && uploadRef.current?.click()}
                        className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition ${dragOver ? "border-amber-500 bg-amber-50" : "border-slate-300 bg-slate-50 hover:border-amber-400"}`}
                        data-testid="upload-zone"
                      >
                        <FileSpreadsheet className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                        <p className="text-sm font-medium">Arrastrá un .xlsx o .csv acá, o hacé click para elegirlo</p>
                        <p className="text-xs text-slate-500 mt-1">Columna A = teléfono, columna B = nombre (opcional). Máx. 5000 contactos.</p>
                        <input ref={uploadRef} type="file" accept=".xlsx,.xls,.csv,.txt" onChange={onUpload} disabled={uploading} className="hidden" data-testid="upload-input" />
                        {uploading && <p className="text-xs text-amber-600 mt-3"><Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Procesando...</p>}
                        {uploadResult && <p className="text-xs text-emerald-600 mt-3" data-testid="upload-result">✓ {uploadResult.count} contactos cargados</p>}
                      </div>
                    </TabsContent>
                  </Tabs>

                  {/* Selected targets preview */}
                  {targetsCount > 0 && (
                    <Card className="bg-amber-50 border-amber-200">
                      <CardContent className="p-3 flex items-center justify-between">
                        <span className="text-sm font-medium text-amber-900" data-testid="targets-count">📨 {targetsCount} destinatario{targetsCount !== 1 ? "s" : ""} listos</span>
                        <Button variant="ghost" size="sm" onClick={() => setSelectedTargets([])} data-testid="clear-targets">Limpiar</Button>
                      </CardContent>
                    </Card>
                  )}

                  <div className="flex justify-end">
                    <Button onClick={() => setStep(2)} disabled={!targetsCount} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="next-step-1">
                      Siguiente: Mensaje <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 2 — Message */}
              {step === 2 && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold">Mensaje</h3>
                    <p className="text-sm text-slate-500">El mismo mensaje se envía a todos los destinatarios.</p>
                  </div>

                  <div>
                    <Label>Nombre interno de la campaña</Label>
                    <Input value={campaignName} onChange={(e) => setCampaignName(e.target.value)} placeholder="Promo BlackFriday 2026" data-testid="campaign-name" />
                  </div>

                  <div>
                    <Label>Tipo de contenido</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {MEDIA_TYPES.map((t) => {
                        const Icon = t.icon;
                        const active = mediaType === t.id;
                        return (
                          <button key={t.id} onClick={() => setMediaType(t.id)} className={`px-3 py-2 rounded-lg border text-sm flex items-center gap-2 transition ${active ? "border-amber-500 bg-amber-50 text-amber-700" : "border-slate-200 hover:border-slate-300"}`} data-testid={`media-type-${t.id}`}>
                            <Icon className="w-4 h-4" /> {t.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {mediaType !== "text" && (
                    <div>
                      <Label>Archivo ({mediaType}) — arrastrá o subí</Label>
                      <MediaUploadInput
                        value={mediaUrl}
                        onChange={setMediaUrl}
                        accept={
                          mediaType === "image" ? "image/*" :
                          mediaType === "video" ? "video/*" :
                          mediaType === "audio" ? "audio/*" :
                          "application/pdf,application/msword,application/vnd.openxmlformats-officedocument.*"
                        }
                        placeholder="https://... o arrastrá un archivo"
                        testIdPrefix="broadcast-media"
                      />
                      <p className="text-xs text-slate-500 mt-1">Subido a Media Library · máx. 50 MB</p>
                    </div>
                  )}

                  <div>
                    <Label className="flex items-center justify-between">
                      <span>Mensaje (texto / caption)</span>
                      <span className="text-xs text-slate-500">{message.length} caracteres</span>
                    </Label>
                    <Textarea rows={6} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Hola {{nombre}}, ..." data-testid="message-text" />
                  </div>

                  <Card className="bg-gradient-to-br from-violet-50 to-pink-50 border-violet-200">
                    <CardContent className="p-4 space-y-2">
                      <p className="text-sm font-semibold flex items-center gap-2 text-violet-900"><Sparkles className="w-4 h-4" /> Generar con IA (Claude)</p>
                      <Input value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} placeholder="Ej: Anuncia 30% off en zapatillas hasta el viernes" data-testid="ai-prompt" />
                      <Button onClick={generateWithAI} disabled={aiBusy || !aiPrompt.trim()} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="ai-generate">
                        {aiBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                        Generar con IA
                      </Button>
                    </CardContent>
                  </Card>

                  <div className="flex justify-between pt-2">
                    <Button variant="ghost" onClick={() => setStep(1)} data-testid="prev-step-2"><ChevronLeft className="w-4 h-4 mr-1" /> Atrás</Button>
                    <Button onClick={() => setStep(3)} disabled={!message.trim() && !mediaUrl} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="next-step-2">
                      Siguiente: Revisar <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 3 — Review & send */}
              {step === 3 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Revisar y enviar</h3>

                  <Card><CardContent className="p-4 grid sm:grid-cols-3 gap-4 text-sm">
                    <div><span className="text-slate-500 block text-xs uppercase">Destinatarios</span><span className="font-bold text-2xl">{targetsCount}</span></div>
                    <div><span className="text-slate-500 block text-xs uppercase">Tipo</span><span className="font-medium capitalize">{mediaType}</span></div>
                    <div><span className="text-slate-500 block text-xs uppercase">Tiempo aprox.</span><span className="font-medium">~{Math.round((targetsCount * 6.5) / 60)} min</span></div>
                  </CardContent></Card>

                  <Card className="bg-emerald-50 border-emerald-200">
                    <CardContent className="p-4">
                      <p className="text-xs uppercase font-bold text-emerald-700 mb-2">Vista previa del mensaje</p>
                      {mediaUrl && <p className="text-xs font-mono text-emerald-800 truncate mb-2">📎 {mediaUrl}</p>}
                      <p className="text-sm whitespace-pre-wrap">{message || <em className="text-slate-400">(sin texto)</em>}</p>
                    </CardContent>
                  </Card>

                  <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg">
                    ⏱️ Delay humanizado: <strong>3-10 segundos random</strong> entre envíos para reducir riesgo de baneo de WhatsApp.
                  </div>

                  {/* Scheduling */}
                  <Card className="bg-violet-50/40 border-violet-200">
                    <CardContent className="p-4 space-y-3">
                      <p className="text-sm font-semibold flex items-center gap-2 text-violet-900">
                        <Calendar className="w-4 h-4" /> ¿Cuándo enviar?
                      </p>
                      <div className="flex gap-2 flex-wrap">
                        <button onClick={() => setSendMode("now")} className={`px-3 py-2 rounded-lg border text-sm flex items-center gap-2 transition ${sendMode === "now" ? "border-violet-500 bg-violet-100 text-violet-800" : "border-slate-200"}`} data-testid="send-mode-now">
                          <Send className="w-4 h-4" /> Enviar ahora
                        </button>
                        <button onClick={() => setSendMode("schedule")} className={`px-3 py-2 rounded-lg border text-sm flex items-center gap-2 transition ${sendMode === "schedule" ? "border-violet-500 bg-violet-100 text-violet-800" : "border-slate-200"}`} data-testid="send-mode-schedule">
                          <Clock className="w-4 h-4" /> Programar
                        </button>
                      </div>
                      {sendMode === "schedule" && (
                        <div className="grid sm:grid-cols-3 gap-3 pt-1">
                          <div>
                            <Label className="text-xs">Fecha</Label>
                            <Input type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} min={new Date().toISOString().slice(0, 10)} data-testid="schedule-date" />
                          </div>
                          <div>
                            <Label className="text-xs">Hora</Label>
                            <Input type="time" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} data-testid="schedule-time" />
                          </div>
                          <div>
                            <Label className="text-xs">Zona horaria</Label>
                            <select value={scheduleTz} onChange={(e) => setScheduleTz(e.target.value)} className="w-full rounded border border-slate-200 p-2 text-sm" data-testid="schedule-tz">
                              {tzOptions.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                            </select>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {error && <p className="text-sm text-red-500" data-testid="broadcast-error">⚠️ {error}</p>}

                  <div className="flex justify-between pt-2">
                    <Button variant="ghost" onClick={() => setStep(2)} data-testid="prev-step-3"><ChevronLeft className="w-4 h-4 mr-1" /> Atrás</Button>
                    <Button onClick={submitCampaign} disabled={submitting || !targetsCount} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="send-campaign-btn">
                      {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> :
                       sendMode === "schedule" ? <Calendar className="w-4 h-4 mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                      {sendMode === "schedule" ? `Programar para ${targetsCount}` : `Enviar a ${targetsCount} destinatario${targetsCount !== 1 ? "s" : ""}`}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== HISTORY ==================== */}
        <TabsContent value="history" className="mt-6 space-y-3">
          {campaigns.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-slate-500" data-testid="history-empty">
              No tenés campañas todavía. Creá una desde la pestaña "Nueva campaña".
            </CardContent></Card>
          ) : campaigns.map((c) => (
            <CampaignCard key={c.id} campaign={c} onView={() => setDetailOpen(c.id)} onRefresh={() => setRefreshTick((x) => x + 1)} />
          ))}
        </TabsContent>
      </Tabs>

      {detailOpen && <CampaignDetailDialog campaignId={detailOpen} onClose={() => setDetailOpen(null)} onRefresh={() => setRefreshTick((x) => x + 1)} />}
    </div>
  );
}

// ==========================================================================
function SelectableList({ items, selected, onToggle, onAll, onClear, testid }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => items.filter((i) => i.label?.toLowerCase().includes(q.toLowerCase()) || i.sub?.toLowerCase().includes(q.toLowerCase())), [items, q]);
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar..." className="flex-1" />
        <Button variant="outline" size="sm" onClick={onAll} data-testid={`${testid}-select-all`}>Todos</Button>
        <Button variant="outline" size="sm" onClick={onClear} data-testid={`${testid}-clear`}>Ninguno</Button>
      </div>
      <div className="border rounded-lg max-h-72 overflow-y-auto divide-y" data-testid={testid}>
        {filtered.length === 0 ? (
          <p className="p-4 text-sm text-slate-400 text-center">Sin resultados</p>
        ) : filtered.map((i) => {
          const checked = selected.includes(i.id);
          return (
            <button key={i.id} onClick={() => onToggle(i.id)} className={`w-full flex items-center gap-3 p-3 text-left text-sm hover:bg-slate-50 transition ${checked ? "bg-amber-50" : ""}`}>
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${checked ? "bg-amber-500 border-amber-500" : "border-slate-300"}`}>
                {checked && <Check className="w-3.5 h-3.5 text-white" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{i.label}</p>
                {i.sub && <p className="text-xs text-slate-500 truncate">{i.sub}</p>}
              </div>
            </button>
          );
        })}
      </div>
      <p className="text-xs text-slate-500">{selected.length} de {items.length} seleccionados</p>
    </div>
  );
}

// ==========================================================================
function CampaignCard({ campaign, onView, onRefresh }) {
  const s = campaign.stats || {};
  const total = s.total || 1;
  const sentPct = ((s.sent || 0) / total) * 100;
  const failedPct = ((s.failed || 0) / total) * 100;
  const statusColors = {
    queued: "bg-slate-100 text-slate-700",
    scheduled: "bg-violet-100 text-violet-700",
    running: "bg-amber-100 text-amber-700",
    completed: "bg-emerald-100 text-emerald-700",
    cancelled: "bg-slate-200 text-slate-600",
    error: "bg-red-100 text-red-700",
  };

  const onDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm("¿Eliminar esta campaña y todo su historial?")) return;
    await authAxios.delete(`${API}/broadcast/campaigns/${campaign.id}`);
    onRefresh();
  };
  const onRetry = async (e) => {
    e.stopPropagation();
    await authAxios.post(`${API}/broadcast/campaigns/${campaign.id}/retry-failed`);
    onRefresh();
  };
  const onCancel = async (e) => {
    e.stopPropagation();
    if (!window.confirm("¿Cancelar esta campaña programada?")) return;
    await authAxios.post(`${API}/broadcast/campaigns/${campaign.id}/cancel`);
    onRefresh();
  };
  const isScheduled = campaign.status === "scheduled" && campaign.scheduled_at;

  return (
    <Card className="hover:shadow-md cursor-pointer transition" onClick={onView} data-testid={`campaign-${campaign.id}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold truncate">{campaign.name}</h4>
              <Badge className={statusColors[campaign.status] || statusColors.queued}>{campaign.status}</Badge>
            </div>
            <p className="text-xs text-slate-500 truncate">{campaign.message?.slice(0, 100) || "(sin texto)"}</p>
          </div>
          <div className="flex gap-1 shrink-0">
            <Button variant="ghost" size="icon" onClick={onView} title="Ver detalle"><Eye className="w-4 h-4" /></Button>
            {isScheduled && <Button variant="ghost" size="icon" onClick={onCancel} title="Cancelar programación" data-testid={`campaign-cancel-${campaign.id}`}><Ban className="w-4 h-4 text-amber-600" /></Button>}
            {(s.failed || 0) > 0 && <Button variant="ghost" size="icon" onClick={onRetry} title="Reintentar fallidos"><RefreshCw className="w-4 h-4" /></Button>}
            <Button variant="ghost" size="icon" onClick={onDelete} title="Eliminar"><Trash2 className="w-4 h-4 text-red-500" /></Button>
          </div>
        </div>

        {isScheduled && (
          <p className="mt-2 text-xs text-violet-700 flex items-center gap-1.5" data-testid={`campaign-scheduled-${campaign.id}`}>
            <Calendar className="w-3 h-3" /> Programada para {new Date(campaign.scheduled_at).toLocaleString()} ({campaign.timezone || "UTC"})
          </p>
        )}

        <div className="mt-3 h-2 rounded-full bg-slate-200 overflow-hidden flex">
          <div className="bg-emerald-500" style={{ width: `${sentPct}%` }} />
          <div className="bg-red-500" style={{ width: `${failedPct}%` }} />
        </div>
        <div className="flex justify-between text-xs text-slate-500 mt-1">
          <span>{s.sent || 0} enviados · {s.failed || 0} fallidos · {s.pending || 0} pendientes</span>
          <span>Total: {s.total || 0}</span>
        </div>
      </CardContent>
    </Card>
  );
}

// ==========================================================================
function CampaignDetailDialog({ campaignId, onClose, onRefresh }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let live = true;
    const fetchOne = () => authAxios.get(`${API}/broadcast/campaigns/${campaignId}`).then(({ data }) => live && setData(data));
    fetchOne();
    const t = setInterval(fetchOne, 3000);
    return () => { live = false; clearInterval(t); };
  }, [campaignId]);

  if (!data) return null;
  const { campaign, deliveries } = data;
  return (
    <Dialog open={!!campaignId} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{campaign.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-4 gap-3 text-center text-sm">
            <Card><CardContent className="p-3"><p className="text-xs text-slate-500">Total</p><p className="font-bold text-xl">{campaign.stats?.total || 0}</p></CardContent></Card>
            <Card><CardContent className="p-3"><p className="text-xs text-emerald-600">Enviados</p><p className="font-bold text-xl text-emerald-600">{campaign.stats?.sent || 0}</p></CardContent></Card>
            <Card><CardContent className="p-3"><p className="text-xs text-red-500">Fallidos</p><p className="font-bold text-xl text-red-500">{campaign.stats?.failed || 0}</p></CardContent></Card>
            <Card><CardContent className="p-3"><p className="text-xs text-amber-500">Pendientes</p><p className="font-bold text-xl text-amber-500">{campaign.stats?.pending || 0}</p></CardContent></Card>
          </div>
          <div className="text-xs bg-slate-50 p-3 rounded-lg whitespace-pre-wrap">{campaign.message}</div>
          <div className="border rounded-lg max-h-80 overflow-y-auto divide-y" data-testid="deliveries-list">
            {deliveries.map((d) => (
              <div key={d.id} className="p-2 text-xs flex items-center gap-2">
                {d.status === "sent" && <Check className="w-4 h-4 text-emerald-500 shrink-0" />}
                {d.status === "failed" && <X className="w-4 h-4 text-red-500 shrink-0" />}
                {d.status === "pending" && <Loader2 className="w-4 h-4 text-amber-500 animate-spin shrink-0" />}
                {d.status === "sending" && <Loader2 className="w-4 h-4 text-blue-500 animate-spin shrink-0" />}
                <span className="font-medium flex-1 truncate">{d.label || d.phone}</span>
                <span className="text-slate-500 truncate">{d.phone || d.target_jid}</span>
                {d.error && <span className="text-red-500 truncate ml-2 max-w-[200px]" title={d.error}>⚠ {d.error.slice(0, 30)}</span>}
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
